import React, { useEffect, useState } from "react";
import { FiClipboard, FiClock, FiActivity, FiCheckCircle, FiAlertCircle, FiFileText, FiSend, FiList, FiRotateCcw } from "react-icons/fi";
import api from "../../api";
import "./FormAssignmentSummaryCards.css";

// Phase 4C, §19 (compact redesign). Reads GET /form-assignment-dashboard/
// summary unchanged -- this component only changed HOW the same numbers are
// rendered (a compact horizontal stat bar instead of oversized individual
// Bootstrap cards), never what is fetched or how it's computed.
//
// `onTotalClick` is the ONLY wired click-to-filter behavior: the table's
// own status filter (form_assignments.status: DRAFT/ACTIVE/CLOSED/
// CANCELLED/ARCHIVED) is a different axis than these summary metrics
// (recipient-progress: Pending/In Progress/Submitted/Overdue, derived from
// form_assignment_users), so there is no existing single-status value that
// a "Pending"/"Overdue" click could correctly apply to the assignments
// table's filter without inventing new filtering logic -- which this task
// explicitly does not want. "Total"/"Assigned By Me" safely maps to
// "clear the status filter" (an already-existing, always-correct action),
// so that one is wired; the rest remain visually compact but static.
const ICONS = {
  total: FiClipboard,
  published: FiFileText,
  all: FiList,
  pending: FiClock,
  inProgress: FiActivity,
  submitted: FiCheckCircle,
  overdue: FiAlertCircle,
  reviewQueue: FiSend,
  needsChanges: FiRotateCcw,
};

// Every stat this component can render, keyed by a stable id so a caller
// can name a subset via `primaryKeys` without duplicating the underlying
// fetch/compute logic. Order here is also the default (full, unsplit)
// render order -- unchanged from before this id-based refactor.
const STAT_DEFS = [
  { id: "admin.totalForms", group: "admin", field: "totalForms", icon: "total", label: "Total Forms" },
  { id: "admin.publishedForms", group: "admin", field: "publishedForms", icon: "published", label: "Published Forms" },
  { id: "admin.allAssignments", group: "admin", field: "allAssignments", icon: "all", label: "All Assignments", clearOnClick: true },
  { id: "admin.pendingReviews", group: "admin", field: "pendingReviews", icon: "pending", label: "Pending Reviews", tone: "warning" },
  { id: "admin.submittedForms", group: "admin", field: "submittedForms", icon: "submitted", label: "Submitted Forms", tone: "success" },
  { id: "admin.formReviewQueue", group: "admin", field: "formReviewQueue", icon: "reviewQueue", label: "Review Queue", tone: "warning" },
  { id: "assigner.assignedByMe", group: "assigner", field: "assignedByMe", icon: "total", label: "Assigned By Me", tone: "info", clearOnClick: true },
  { id: "assigner.pendingReviews", group: "assigner", field: "pendingReviews", icon: "pending", label: "Pending", tone: "warning" },
  { id: "assigner.inProgressReviews", group: "assigner", field: "inProgressReviews", icon: "inProgress", label: "In Progress", tone: "info" },
  { id: "assigner.submittedReviews", group: "assigner", field: "submittedReviews", icon: "submitted", label: "Submitted", tone: "success" },
  { id: "assigner.overdueReviews", group: "assigner", field: "overdueReviews", icon: "overdue", label: "Overdue", tone: "danger" },
  // `meta` is the short supporting line the "tech" variant (below) prints
  // under the number -- static descriptive text, never a computed figure.
  { id: "assignee.reviewFormsPending", group: "assignee", field: "reviewFormsPending", icon: "pending", label: "To Review: Pending", tone: "warning", meta: "Awaiting your action" },
  { id: "assignee.reviewFormsInProgress", group: "assignee", field: "reviewFormsInProgress", icon: "inProgress", label: "To Review: In Progress", tone: "info", meta: "Being worked on" },
  { id: "assignee.reviewFormsNeedsChanges", group: "assignee", field: "reviewFormsNeedsChanges", icon: "needsChanges", label: "To Review: Needs Changes", tone: "warning", meta: "Returned for correction" },
  { id: "assignee.reviewFormsSubmitted", group: "assignee", field: "reviewFormsSubmitted", icon: "submitted", label: "To Review: Submitted", tone: "success", meta: "Sent for review" },
  { id: "assignee.reviewFormsOverdue", group: "assignee", field: "reviewFormsOverdue", icon: "overdue", label: "To Review: Overdue", tone: "danger", meta: "Past the due date" },
];

function StatItem({ icon, label, value, tone = "neutral", clickable, active, onClick, group }) {
  const Icon = ICONS[icon] || FiClipboard;
  return (
    <button
      type="button"
      title={group}
      className={`fas-stat puws-stat fas-stat-${tone} ${clickable ? "fas-stat-clickable" : ""} ${active ? "fas-stat-active" : ""}`}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
    >
      <Icon className="fas-stat-icon" aria-hidden="true" />
      <span className="fas-stat-value puws-stat-val">{value ?? 0}</span>
      <span className="fas-stat-label puws-stat-lbl">{label}</span>
    </button>
  );
}

// Opt-in `variant="tech"` card: the Technician Ticket Dashboard's KPI card
// (TechnicianDesignSystem.css `.tech-kpi` -- white card, coloured top border,
// icon badge, label / value / meta). Only meaningful inside a `.tech-ds`
// page root; every other caller keeps StatItem above, unchanged.
function TechStatItem({ icon, label, value, meta, tone = "neutral", clickable, active, onClick, group }) {
  const Icon = ICONS[icon] || FiClipboard;
  const body = (
    <>
      <div className="tech-kpi-icon" aria-hidden="true">
        <Icon />
      </div>
      <div className="tech-kpi-body">
        <div className="tech-kpi-label" title={label}>{label}</div>
        <div className="tech-kpi-value">{value ?? 0}</div>
        {meta ? <small className="tech-kpi-meta">{meta}</small> : null}
      </div>
    </>
  );
  const className = `tech-kpi tech-tone-${tone}${active ? " fas-tech-active" : ""}`;

  if (clickable) {
    return (
      <button type="button" title={group} className={`${className} fas-tech-clickable`} onClick={onClick}>
        {body}
      </button>
    );
  }
  return (
    <div title={group} className={className}>
      {body}
    </div>
  );
}

const GROUP_TITLES = { admin: "Admin Overview", assigner: "Assigned By Me", assignee: "Forms To Review" };

// Compact redesign: the SAME three data groups (admin/assigner/assignee),
// the SAME fields, the SAME single wired click-to-filter behavior --
// rendered as ONE flat row of chips instead of three separate titled/
// bordered sections stacked vertically. Nothing about what is fetched or
// computed changed, only how many wrapper elements the same numbers sit
// inside. Each chip's `title` attribute (a native tooltip) keeps the
// original group name discoverable without spending vertical space on it.
//
// Forms Workspace polish pass: callers embedding this inside a compact tab
// (rather than a full standalone page) can now pass `primaryKeys` -- a
// subset of STAT_DEFS ids -- to show just those up front and push every
// other already-fetched metric behind a "More Metrics" toggle, instead of
// all 6-15 cards at equal visual weight. Omitting `primaryKeys` (every
// existing caller before this pass) renders exactly as before: every
// available stat, in the same order, with no toggle at all.
export default function FormAssignmentSummaryCards({
  statusFilterActive,
  onClearStatusFilter,
  primaryKeys,
  extraPrimaryStats,
  labelOverrides,
  excludeKeys,
  variant,
}) {
  const [summary, setSummary] = useState(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    api
      .get("/form-assignment-dashboard/summary")
      .then((res) => setSummary(res.data?.data || null))
      .catch((err) => console.error("Fetch assignment dashboard summary error:", err));
  }, []);

  if (!summary) return null;
  const isClearActive = !statusFilterActive;

  const available = STAT_DEFS.filter((def) => summary[def.group] && !excludeKeys?.includes(def.id));

  const isTech = variant === "tech";
  const Stat = isTech ? TechStatItem : StatItem;

  const renderStat = (def) => (
    <Stat
      key={def.id}
      group={GROUP_TITLES[def.group]}
      icon={def.icon}
      label={labelOverrides?.[def.id] || def.label}
      value={summary[def.group][def.field]}
      meta={isTech ? def.meta : undefined}
      tone={def.tone}
      clickable={def.clearOnClick && !!onClearStatusFilter}
      active={def.clearOnClick && isClearActive}
      onClick={def.clearOnClick ? onClearStatusFilter : undefined}
    />
  );

  // No primaryKeys -> unchanged, full, unsplit rendering (byte-identical to
  // this component's behavior before this pass).
  if (isTech) {
    const primaryDefs = primaryKeys
      ? primaryKeys.map((id) => available.find((def) => def.id === id)).filter(Boolean)
      : available;
    const secondaryDefs = primaryKeys ? available.filter((def) => !primaryKeys.includes(def.id)) : [];
    const extras = extraPrimaryStats || [];

    return (
      <div className="fas-tech">
        <div className="tech-kpis tech-kpis--4">
          {extras.map((s) => (
            <TechStatItem key={s.id} group={s.group} icon={s.icon} label={s.label} value={s.value} meta={s.meta} tone={s.tone} />
          ))}
          {primaryDefs.map(renderStat)}
        </div>
        {secondaryDefs.length > 0 && (
          <>
            <div className="fas-tech-more-row">
              <button
                type="button"
                className="fas-tech-more-toggle"
                aria-expanded={showMore}
                onClick={() => setShowMore((v) => !v)}
              >
                <i className={`fa-solid ${showMore ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true"></i>
                {showMore ? "Hide More Metrics" : "More Metrics"}
              </button>
            </div>
            {showMore && <div className="tech-kpis tech-kpis--4 fas-tech-secondary">{secondaryDefs.map(renderStat)}</div>}
          </>
        )}
      </div>
    );
  }

  if (!primaryKeys) {
    return (
      <div className="fas-wrap">
        <div className="fas-bar">
          <div className="fas-bar-items">{available.map(renderStat)}</div>
        </div>
      </div>
    );
  }

  const primaryDefs = primaryKeys
    .map((id) => available.find((def) => def.id === id))
    .filter(Boolean);
  const secondaryDefs = available.filter((def) => !primaryKeys.includes(def.id));
  const extras = extraPrimaryStats || [];

  return (
    <div className="fas-wrap">
      <div className="fas-bar">
        <div className="fas-bar-items">
          {extras.map((s) => (
            <StatItem key={s.id} group={s.group} icon={s.icon} label={s.label} value={s.value} tone={s.tone} />
          ))}
          {primaryDefs.map(renderStat)}
        </div>
        {secondaryDefs.length > 0 && (
          <>
            <button type="button" className="fas-more-toggle" onClick={() => setShowMore((v) => !v)}>
              <i className={`fa-solid ${showMore ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true"></i>
              {showMore ? "Hide More Metrics" : "More Metrics"}
            </button>
            {showMore && <div className="fas-bar-items fas-bar-items-secondary">{secondaryDefs.map(renderStat)}</div>}
          </>
        )}
      </div>
    </div>
  );
}
